import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../../config/baseUrl";
import axios from "axios";
import toastify from "../../config/toastify";

const PAGE_TYPE_OPTIONS = [
  { value: "homepage", label: "Homepage" },
  { value: "testpage", label: "Testpage" },
];

export default function DailyMockBannerFormPage({
  mode,
  pageTitle,
  breadcrumbTitle,
  backPath,
  successMessage,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formval, setFormval] = useState({
    name: "",
    pageType: "homepage",
    status: "active",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [pageLoading, setPageLoading] = useState(mode === "edit");

  const changeHandler = (e) => {
    setFormval((preVal) => ({ ...preVal, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    if (mode !== "edit" || !id) {
      return;
    }

    const fetchBanner = async () => {
      try {
        setPageLoading(true);
        const response = await axios.get(`${baseUrl}/banner/${id}`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        });

        const bannerData = response?.data?.data;
        if (!bannerData) {
          toastify.error("Banner not found");
          navigate(backPath);
          return;
        }

        const isDailyMockBanner = bannerData?.bannerFor === "daily_mock" || bannerData?.type === "daily_mock";
        if (!isDailyMockBanner) {
          toastify.error("Banner type mismatch");
          navigate(backPath);
          return;
        }

        setFormval({
          name: bannerData?.name || "",
          pageType:
            bannerData?.pageType ||
            (bannerData?.type === "homepage" || bannerData?.type === "testpage"
              ? bannerData.type
              : "homepage"),
          status: bannerData?.status || "active",
        });
        setExistingImage(bannerData?.image ? `${baseUrl}/${bannerData.image}` : "");
      } catch (error) {
        console.error("Error :", error);
        toastify.error("Failed to fetch banner");
        navigate(backPath);
      } finally {
        setPageLoading(false);
      }
    };

    fetchBanner();
  }, [backPath, id, mode, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formval.name) {
      return toastify.error("Name is required");
    }

    if (!formval.pageType) {
      return toastify.error("Type is required");
    }

    if (mode === "add" && !selectedFile) {
      return toastify.error("Image is required");
    }

    const formData = new FormData();
    formData.append("name", formval.name);
    formData.append("type", formval.pageType);
    formData.append("bannerFor", "daily_mock");
    formData.append("status", formval.status);

    if (selectedFile) {
      formData.append("image", selectedFile);
    }

    try {
      const response =
        mode === "edit"
          ? await axios.put(`${baseUrl}/banner/${id}`, formData, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            })
          : await axios.post(`${baseUrl}/banner`, formData, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            });

      if (!response?.data?.status) {
        toastify.error(response?.data?.message || "Some error occurred");
        return;
      }

      toastify.success(successMessage);
      navigate(backPath);
    } catch (error) {
      console.error("Error :", error);
      toastify.error(error?.response?.data?.message || "Server error");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  if (pageLoading) {
    return (
      <section className="main-sec">
        <div className="row justify-content-center">
          <div className="col-lg-6 text-center py-5">
            <h5>Loading...</h5>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="main-sec">
      <div className="row align-items-center">
        <div className="col-lg-6">
          <div className="dashboard-title">
            <h4 className="dash-head">
              <i className="fa fa-users me-2" />
              {pageTitle}
            </h4>
          </div>
          <div className="custom-bredcump">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {breadcrumbTitle}
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="col-lg-6 d-flex justify-content-end">
          <Link to={backPath} className="btn btn-info text-white">
            <i className="fa fa-arrow-left me-1"></i>
            Back
          </Link>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-12">
          <div className="cards edit-usr">
            <form action="" onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-6 mb-4">
                  <label htmlFor="name" className="form-label">
                    Name<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    placeholder="Enter Name"
                    onChange={changeHandler}
                    value={formval?.name}
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label htmlFor="pageType" className="form-label">
                    Type<sup className="text-danger">*</sup>
                  </label>
                  <select
                    className="form-control"
                    name="pageType"
                    onChange={changeHandler}
                    value={formval?.pageType}
                  >
                    {PAGE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-lg-6 mb-4">
                  <label htmlFor="status" className="form-label">
                    Status<sup className="text-danger">*</sup>
                  </label>
                  <select
                    className="form-control"
                    name="status"
                    onChange={changeHandler}
                    value={formval?.status}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="col-lg-12 mb-4">
                  <label htmlFor="image" className="form-label">
                    Banner Image{mode === "add" ? <sup className="text-danger">*</sup> : null}
                  </label>
                  <div
                    className={`drag-drop-area ${isDragging ? "dragging" : ""}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      border: "2px dashed #ccc",
                      borderRadius: "8px",
                      padding: "20px",
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    {!selectedFile && !previewUrl && !existingImage ? (
                      <>
                        <p className="mb-2">Drag & Drop your image here</p>
                        <p className="mb-2">or</p>
                      </>
                    ) : null}

                    <input
                      type="file"
                      className="form-control"
                      id="image"
                      name="image"
                      accept="image/*"
                      onChange={handleFileChange}
                    />

                    {(previewUrl || existingImage) && (
                      <div className="mt-3">
                        <img
                          src={previewUrl || existingImage}
                          alt="banner preview"
                          style={{ maxWidth: "240px", maxHeight: "160px", objectFit: "cover" }}
                        />
                        {selectedFile && (
                          <div className="mt-2">
                            <button type="button" className="btn btn-sm btn-danger" onClick={removeFile}>
                              Remove Selected File
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedFile && (
                      <p className="mt-2 mb-0">
                        {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                      </p>
                    )}
                  </div>
                </div>

                <div className="col-lg-12">
                  <button type="submit" className="btn btn-for-add text-white">
                    {mode === "edit" ? "Update Banner" : "Add Banner"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
